import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, getCurrentUser, login as apiLogin, logout as apiLogout, register as apiRegister, getToken } from '../api/auth';

interface AuthContextType {
    user: User | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    isAdmin: boolean;
    login: (username: string, password: string) => Promise<{ success: boolean; message: string }>;
    register: (username: string, email: string, password: string) => Promise<{ success: boolean; message: string }>;
    logout: (clearMessagingSessions?: boolean) => Promise<void>;
    refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const refreshUser = async () => {
        try {
            const result = await getCurrentUser();
            if (result.valid && result.user) {
                setUser(result.user);
            } else {
                setUser(null);
            }
        } catch (err) {
            setUser(null);
        }
    };

    useEffect(() => {
        const initAuth = async () => {
            if (getToken()) {
                await refreshUser();
            }
            setIsLoading(false);
        };
        initAuth();
    }, []);

    const login = async (username: string, password: string) => {
        const result = await apiLogin(username, password);
        if (result.success && result.user) {
            setUser(result.user);
        }
        return { success: result.success, message: result.message };
    };

    const register = async (username: string, email: string, password: string) => {
        const result = await apiRegister(username, email, password);
        return { success: result.success, message: result.message };
    };

    const logout = async (clearMessagingSessions: boolean = true) => {
        await apiLogout(clearMessagingSessions);
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{
            user,
            isLoading,
            isAuthenticated: !!user,
            isAdmin: user?.role === 'admin',
            login,
            register,
            logout,
            refreshUser
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
